'use client';
import { classNames, meater } from '@repo/ui/src/utils/classNames';
import Section from '@repo/ui/src/components/Section';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { HeaderVariationProps } from '@repo/ui/src/types/headerVariation';
import { AmplienceButtonProps } from '@repo/ui/src/types/button';
import TextContentColumn, { TextContentColumnProps } from './_Column';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import { MetaProps } from '@repo/ui/src/types/meta';
import Image from 'next/image';
import { useIsMD } from '@repo/ui/src/utils/useMediaQuery';
import { ImageProps } from '@repo/ui/src/types';

export enum BackgroundTheme {
  Dark = 'Dark',
  Black = 'Black',
  DarkGray = 'Dark Gray',
}

export enum HeaderPosition {
  top = 'top',
  left = 'left',
}

type Align = 'left' | 'center' | 'right';

export interface Props {
  heading: string;
  headerVariation: HeaderVariationProps;
  headerPosition: HeaderPosition;
  align: Align;
  darkTheme: boolean;
  headerDivider: boolean;
  noBackground: boolean;
  showLogImage: boolean;
  numColumns: number;
  midNumColumns: number;
  button: AmplienceButtonProps;
  columns: Array<TextContentColumnProps>;
  _meta: MetaProps;
  backgroundTheme: BackgroundTheme;
  showBackgroundTexture: boolean;
  _testID?: string;
  image?: ImageProps;
}

const TextContentColumns = ({
  heading,
  headerVariation,
  align,
  columns,
  darkTheme,
  button,
  headerPosition,
  headerDivider,
  numColumns = 3,
  midNumColumns,
  noBackground,
  showLogImage,
  _meta,
  showBackgroundTexture,
  backgroundTheme,
  _testID,
  image,
}: Props) => {
  const mdAndUp = useIsMD();
  const dark = backgroundTheme === BackgroundTheme.Dark || darkTheme || noBackground;
  const black = backgroundTheme === BackgroundTheme.Black;
  const darkGray = backgroundTheme === BackgroundTheme.DarkGray;
  const styledAsDarkTheme = dark || black || darkGray;
  const hasCornerImage = !!image?.image?.endpoint;
  const isTitledContent =
    _meta?.schema ===
    'https://traegergrills.com/data/contentTypes/titledContentMultipleColumns.json';
  const actuallyDisplayLogImage = showLogImage || (!dark && isTitledContent);

  const displayLargeLogImage = actuallyDisplayLogImage && columns.length > 3;

  if (!columns) return null;

  return (
    <Section
      gutterX
      gutterY
      basePadding
      dark={dark}
      black={black}
      headerPosition={headerPosition}
      texture={isTitledContent || showBackgroundTexture}
      darkGray={darkGray}
      data-test-id={`text_content_columns_${_testID}`}
      className={classNames(
        'tw-overflow-hidden tw-flex tw-flex-col md:tw-flex-row',
        headerPosition === HeaderPosition.top && 'tw-flex-wrap tw-justify-center',
        meater('sm:tw-pt-32 sm:tw-pb-64')
      )}
    >
      {actuallyDisplayLogImage && !hasCornerImage && (
        <div
          className={classNames(
            'tw-absolute -tw-z-10 -tw-left-[20px] -tw-top-[20px] tw-scale-y-[-1] sm:-tw-left-[50px] sm:-tw-top-[50px] md:tw-transform-none md:tw-top-auto tw-aspect-square',
            displayLargeLogImage
              ? 'tw-max-w-[170px] sm:tw-max-w-[340px] md:tw-bottom-[50px] md:tw-w-[500px]'
              : 'md:-tw-bottom-[50px] tw-w-[170px] md:tw-w-[340px]',
          )}
        >
          <Image
            src={buildImageSrc(
              {
                endpoint: 'traeger',
                name: 'log-background',
              },
              500,
              0,
              0,
            )}
            role="presentation"
            alt=""
            width={500}
            height={500}
            className={classNames(styledAsDarkTheme && 'tw-opacity-[0.15]')}
          />
        </div>
      )}
      {hasCornerImage && (
        <div
          className={classNames(
            'tw-hidden md:tw-block tw-absolute -tw-z-10 -tw-left-[20px] -tw-top-[20px] tw-scale-y-[-1] sm:-tw-left-[50px] sm:-tw-top-[50px] md:tw-transform-none md:tw-top-auto tw-aspect-square',
            displayLargeLogImage
              ? 'tw-max-w-[170px] sm:tw-max-w-[340px] md:tw-bottom-[50px] md:tw-w-[500px]'
              : 'md:-tw-bottom-[50px] tw-w-[170px] md:tw-w-[340px]',
          )}
        >
          <Image
            src={buildImageSrc(image, 500, 0, 0)}
            role="presentation"
            alt=""
            width={500}
            height={500}
            className={classNames(styledAsDarkTheme && 'tw-opacity-[0.15]')}
          />
        </div>
      )}
      <div
        className={classNames(
          'tw-p-[1.5rem] md:tw-pb-4 tw-flex-shrink-2 md:tw-w-[35%]',
          headerPosition === HeaderPosition.top && heading && 'tw-w-full',
          meater('tw-pt-0')
        )}
      >
        {heading && (
          <ToutContent
            headingStyleVariant="h2"
            forceDivider={headerDivider}
            buttons={button?.link?.link ? [button] : undefined}
            dark={styledAsDarkTheme}
            align={mdAndUp ? align : 'left'}
            {...{ heading, headerVariation }}
          />
        )}
      </div>
      <div
        className={classNames(
          headerPosition === HeaderPosition.top ? 'tw-w-auto' : 'tw-w-full',
          'md:tw-grid',
          midNumColumns
            ? `md:tw-grid-cols-${midNumColumns} xl:tw-grid-cols-${numColumns}`
            : `md:tw-grid-cols-${numColumns}`,
        )}
      >
        {columns.map((column: TextContentColumnProps) => (
          <TextContentColumn key={column.heading} {...column} darkTheme={styledAsDarkTheme} />
        ))}
      </div>
    </Section>
  );
};

export default TextContentColumns;
